import { useEffect, useState } from "react";
import { useAuth, API } from "../App";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  BookOpen,
  CheckCircle2,
  Clock,
  AlertCircle,
  Users,
  GraduationCap,
  TrendingUp,
  ArrowRight,
  BarChart3,
  Calendar,
} from "lucide-react";

const StudentDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/student`);
      setData(response.data);
    } catch (error) {
      toast.error("Error al cargar el dashboard");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 skeleton rounded-xl" />
          ))}
        </div>
        <div className="h-96 skeleton rounded-xl" />
      </div>
    );
  }

  const courseMap = data?.courses?.reduce((acc, c) => ({ ...acc, [c.id]: c }), {}) || {};

  return (
    <div className="space-y-8 animate-fade-in" data-testid="student-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-heading">Mi Dashboard</h1>
        <p className="text-slate-600 mt-1">Bienvenido de nuevo. Aquí está tu progreso.</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="kpi-card card-hover" data-testid="kpi-total">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Cursos</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{data?.total_courses || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card card-hover" data-testid="kpi-completed">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Completados</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{data?.completed || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card card-hover" data-testid="kpi-pending">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">En Progreso</p>
                <p className="text-3xl font-bold text-amber-600 mt-1">{data?.pending || 0}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card card-hover" data-testid="kpi-expiring">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Por Vencer</p>
                <p className="text-3xl font-bold text-red-600 mt-1">{data?.expiring_soon || 0}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Course List */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900 font-heading">Mis Cursos</h2>
          <Link to="/courses">
            <Button variant="ghost" className="text-primary" data-testid="view-all-courses-btn">
              Ver todos <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>

        {data?.enrollments?.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No tienes cursos matriculados</h3>
              <p className="text-slate-500 mb-4">Contacta a tu administrador para inscribirte en cursos.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data?.enrollments?.slice(0, 6).map((enrollment, index) => {
              const course = courseMap[enrollment.course_id];
              if (!course) return null;

              const progress = [
                enrollment.pretest_completed,
                enrollment.material_viewed,
                enrollment.posttest_completed
              ].filter(Boolean).length;
              const progressPercent = (progress / 3) * 100;

              return (
                <Link key={enrollment.id} to={`/courses/${course.id}`} data-testid={`course-card-${index}`}>
                  <Card className="course-card h-full">
                    <div className="h-32 bg-gradient-to-br from-primary to-blue-600 relative">
                      <div className="absolute inset-0 bg-black/20" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          enrollment.status === 'completed' ? 'bg-green-500 text-white' :
                          enrollment.status === 'failed' ? 'bg-red-500 text-white' :
                          'bg-white/90 text-slate-700'
                        }`}>
                          {enrollment.status === 'completed' ? 'Completado' :
                           enrollment.status === 'failed' ? 'Reprobado' : 'En Progreso'}
                        </span>
                      </div>
                    </div>
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2">{course.name}</h3>
                      <p className="text-sm text-slate-500 mb-4">{course.category}</p>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Progreso</span>
                          <span className="font-medium text-slate-700">{Math.round(progressPercent)}%</span>
                        </div>
                        <Progress value={progressPercent} className="h-2" />
                      </div>

                      {(enrollment.pretest_score !== null || enrollment.posttest_score !== null) && (
                        <div className="mt-4 pt-4 border-t border-slate-100 flex gap-4 text-sm">
                          {enrollment.pretest_score !== null && (
                            <div>
                              <span className="text-slate-500">Pre-test:</span>
                              <span className="ml-1 font-medium">{enrollment.pretest_score.toFixed(0)}%</span>
                            </div>
                          )}
                          {enrollment.posttest_score !== null && (
                            <div>
                              <span className="text-slate-500">Post-test:</span>
                              <span className="ml-1 font-medium">{enrollment.posttest_score.toFixed(0)}%</span>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const ProfessorDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/professor`);
      setData(response.data);
    } catch (error) {
      toast.error("Error al cargar el dashboard");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 skeleton rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="professor-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-heading">Dashboard del Profesor</h1>
        <p className="text-slate-600 mt-1">Gestiona tus cursos y estudiantes.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="kpi-card card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Mis Cursos</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{data?.total_courses || 0}</p>
              </div>
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Estudiantes</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{data?.total_students || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Completados</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{data?.completed_students || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">En Progreso</p>
                <p className="text-3xl font-bold text-amber-600 mt-1">{data?.in_progress_students || 0}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Mis Cursos</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.courses?.length === 0 ? (
              <div className="text-center py-8">
                <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No tienes cursos creados</p>
                <Link to="/admin/courses">
                  <Button className="mt-4" data-testid="create-course-btn">Crear Curso</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {data?.courses?.map((course) => (
                  <Link key={course.id} to={`/courses/${course.id}`}>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div>
                        <h4 className="font-medium text-slate-900">{course.name}</h4>
                        <p className="text-sm text-slate-500">{course.category}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Acciones Rápidas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/admin/courses" className="block">
              <Button variant="outline" className="w-full justify-start" data-testid="quick-create-course">
                <GraduationCap className="w-4 h-4 mr-3" />
                Crear Nuevo Curso
              </Button>
            </Link>
            <Link to="/admin/enrollments" className="block">
              <Button variant="outline" className="w-full justify-start" data-testid="quick-enrollments">
                <Users className="w-4 h-4 mr-3" />
                Gestionar Matrículas
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/admin`);
      setData(response.data);
    } catch (error) {
      toast.error("Error al cargar el dashboard");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 skeleton rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-heading">Panel de Administración</h1>
        <p className="text-slate-600 mt-1">Vista general del sistema LMS.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="kpi-card card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Usuarios</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{data?.total_users || 0}</p>
              </div>
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
            </div>
            <div className="mt-4 flex gap-4 text-sm">
              <span className="text-slate-500">
                <span className="font-medium text-slate-700">{data?.users_by_role?.ADMIN || 0}</span> Admin
              </span>
              <span className="text-slate-500">
                <span className="font-medium text-slate-700">{data?.users_by_role?.PROFESOR || 0}</span> Prof.
              </span>
              <span className="text-slate-500">
                <span className="font-medium text-slate-700">{data?.users_by_role?.ESTUDIANTE || 0}</span> Est.
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Cursos</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{data?.total_courses || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Matrículas</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{data?.total_enrollments || 0}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Usuarios Recientes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.recent_users?.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center">
                      <Users className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{user.full_name}</p>
                      <p className="text-xs text-slate-500">{user.username}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    user.role === 'ADMIN' ? 'bg-purple-100 text-purple-700' :
                    user.role === 'PROFESOR' ? 'bg-blue-100 text-blue-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {user.role}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Cursos Recientes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.recent_courses?.map((course) => (
                <Link key={course.id} to={`/courses/${course.id}`}>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-green-100 rounded-full flex items-center justify-center">
                        <BookOpen className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{course.name}</p>
                        <p className="text-xs text-slate-500">{course.category}</p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Acciones Rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/admin/users">
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2" data-testid="admin-users-btn">
                <Users className="w-6 h-6" />
                <span>Gestionar Usuarios</span>
              </Button>
            </Link>
            <Link to="/admin/courses">
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2" data-testid="admin-courses-btn">
                <GraduationCap className="w-6 h-6" />
                <span>Gestionar Cursos</span>
              </Button>
            </Link>
            <Link to="/admin/enrollments">
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2" data-testid="admin-enrollments-btn">
                <BarChart3 className="w-6 h-6" />
                <span>Matrículas</span>
              </Button>
            </Link>
            <Link to="/courses">
              <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2" data-testid="admin-view-courses-btn">
                <BookOpen className="w-6 h-6" />
                <span>Ver Cursos</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();

  if (user?.role === "ESTUDIANTE") {
    return <StudentDashboard />;
  } else if (user?.role === "PROFESOR") {
    return <ProfessorDashboard />;
  } else {
    return <AdminDashboard />;
  }
};

export default Dashboard;
